/**
 * Body — the procedural soldier mesh.
 *
 * A humanoid lofted from capped, bevelled cross-sections swept along the rig's bind
 * pose: torso follows the spine curve, limbs follow their bone segments, boots are
 * sculpted volumes. The head is a separate parametric surface (see Head.js) and the
 * hands wrap the carbine in weapon space (see `buildHand`).
 *
 * PROPORTION, measured, not asserted: menton 1.5610, scalp 1.7980, feet 0.000 on a
 * 1.798 m silhouette — **7.51 heads bare, 7.32 with the helmet on**. The previous
 * body measured 5.0 heads in char_hero because a 0.277 m skull sat on a neck that was
 * both wider and further forward than the jaw, so head and neck read as one mass.
 *
 * SKIN WEIGHTS are the hard part and are authored, not guessed: every loft ring
 * carries a weight keyframe list along the limb, interpolated with smoothstep, so a
 * joint gets a wide (±0.10 m) 3-bone blend instead of a 1-bone hard switch. The ring
 * that sits exactly on a joint is 50/50 and carries a small radius bump, which is what
 * stops the elbow/knee pinching to a candy wrapper when the joint folds past 90°.
 * The forearm additionally routes its roll through a dedicated `fTwist` helper bone.
 *
 * owner: characters
 */
import * as THREE from 'three';
import { SLOT, smooth, lerp, clamp, crSample } from './Build.js';
import { HEAD, HEAD_RINGS, HEAD_SIDES, headAngle, skullPoint, faceTint } from './Head.js';
import { LEFT_GRIP_ROT, weaponAnchors } from './Rig.js';

const _t = new THREE.Vector3();
const _x = new THREE.Vector3();
const _y = new THREE.Vector3();
const UP_Z = new THREE.Vector3(0, 0, 1);

/**
 * Interpolate a weight keyframe list.
 * keys: [ [distance, { boneName: weight, ... }], ... ] sorted by distance.
 */
export function makeWeightFn(rig, keys) {
  const idx = (n) => rig.index.get(n) ?? 0;
  const baked = keys.map(([d, m]) => [d, Object.entries(m).map(([n, w]) => [idx(n), w])]);
  return (d) => {
    if (d <= baked[0][0]) return baked[0][1].map((e) => e.slice());
    const last = baked[baked.length - 1];
    if (d >= last[0]) return last[1].map((e) => e.slice());
    let i = 0;
    while (i < baked.length - 2 && baked[i + 1][0] < d) i++;
    const [d0, w0] = baked[i], [d1, w1] = baked[i + 1];
    const t = smooth(d0, d1, d);
    const m = new Map();
    for (const [b, w] of w0) m.set(b, (m.get(b) || 0) + w * (1 - t));
    for (const [b, w] of w1) m.set(b, (m.get(b) || 0) + w * t);
    return [...m.entries()];
  };
}

/**
 * Rings along a polyline with parallel-ish transport frames.
 * `profile(d, t)` -> { rx, ry, round, dx, dz } (dx/dz nudge the section centre).
 */
export function tubeRings(points, profile, weightFn, opts = {}) {
  const up = opts.up || UP_Z;
  const rings = [];
  let d = 0;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (i > 0) d += p.distanceTo(points[i - 1]);
    _t.copy(points[Math.min(points.length - 1, i + 1)]).sub(points[Math.max(0, i - 1)]);
    if (_t.lengthSq() < 1e-10) _t.set(0, 1, 0);
    _t.normalize();
    _x.crossVectors(up, _t);
    if (_x.lengthSq() < 1e-8) _x.set(1, 0, 0);
    _x.normalize();
    _y.crossVectors(_t, _x).normalize();
    const pr = profile(d, i / (points.length - 1));
    const c = p.clone();
    if (pr.dx) c.addScaledVector(_x, pr.dx);
    if (pr.dz) c.addScaledVector(_y, pr.dz);
    rings.push({
      p: c, x: _x.clone(), y: _y.clone(),
      rx: pr.rx, ry: pr.ry, round: pr.round ?? 0.85,
      w: weightFn(d),
    });
  }
  return rings;
}

/** Sample a control polyline into `n` evenly spaced points. */
export function resample(ctrl, n) {
  const pts = ctrl.map((p) => new THREE.Vector3(p[0], p[1], p[2]));
  const out = [];
  for (let i = 0; i < n; i++) out.push(crSample(pts, i / (n - 1), new THREE.Vector3()));
  return out;
}

/** Piecewise-linear profile lookup keyed on ring index parameter t (0..1). */
function profileTable(rows) {
  return (t) => {
    if (t <= rows[0][0]) return rows[0];
    if (t >= rows[rows.length - 1][0]) return rows[rows.length - 1];
    let i = 0;
    while (i < rows.length - 2 && rows[i + 1][0] < t) i++;
    const a = rows[i], b = rows[i + 1];
    const u = smooth(a[0], b[0], t);
    const o = [t];
    for (let k = 1; k < a.length; k++) o.push(lerp(a[k], b[k], u));
    return o;
  };
}

/* ================================================================== *
 * TORSO
 * ================================================================== */
function buildTorso(B, rig, look) {
  const S = rig.scale;
  const ctrl = [
    [0, 0.838, 0.012], [0, 0.900, 0.006], [0, 0.975, 0.002], [0, 1.055, 0.006],
    [0, 1.130, 0.014], [0, 1.205, 0.018], [0, 1.270, 0.014], [0, 1.325, 0.008],
    [0, 1.390, -0.002], [0, 1.424, -0.010], [0, 1.452, -0.012],
    // Standing collar. The shirt used to stop at 1.452 against a menton at 1.561, so
    // 0.109 m of bare neck showed under a 0.2325 m head — 0.47 head-lengths, and the
    // reviewer measured 0.53 off the frame against ~0.30 anatomical. The neck itself is
    // the right length; what was wrong is that a combat uniform's collar was sitting at
    // the base of it instead of high on it. Taking the collar to 1.494 leaves 0.067 m
    // showing, i.e. 0.29 head-lengths, without touching the rig — so nothing in the gait
    // model moves.
    [0, 1.474, -0.013], [0, 1.494, -0.013],
  ].map((p) => [p[0] * S, p[1] * S, p[2] * S]);
  const G = look.girth ?? 1;

  const N = 19;
  const pts = resample(ctrl, N);

  // t, rx, rz, round, centre-z nudge
  /**
   * `t` is arc length along `ctrl`, normalised. Raising the collar lengthened that
   * polyline from 0.614 m to 0.656 m, so EVERY existing row had to be rescaled by
   * 0.614/0.656 = 0.936 or the whole torso profile slides up 4 cm — which it did on the
   * first attempt, and the shoulders came out as one smooth tan cowl over the plate
   * carrier. A table keyed on a normalised parameter is not invariant to extending the
   * curve; that is the trap.
   */
  const prof = profileTable([
    [0.000, 0.148, 0.112, 0.72, 0.000],
    [0.066, 0.163, 0.121, 0.72, 0.000],
    [0.150, 0.161, 0.121, 0.70, -0.004],
    [0.262, 0.142, 0.111, 0.72, -0.002],
    [0.356, 0.137, 0.110, 0.74, 0.004],
    [0.468, 0.150, 0.118, 0.74, 0.006],
    [0.580, 0.170, 0.126, 0.72, 0.004],
    [0.674, 0.182, 0.128, 0.70, 0.000],
    [0.767, 0.181, 0.121, 0.70, -0.004],
    [0.842, 0.161, 0.108, 0.72, -0.006],
    [0.899, 0.118, 0.095, 0.82, -0.004],
    [0.936, 0.087, 0.080, 0.86, 0.000],   // base of the collar band (the old shirt top)
    // The collar band. It has to stay clear of the neck loft, which is rx 0.058 /
    // rz 0.062 at 1.494 — 14 mm of air, so the two surfaces never go near-tangent.
    [0.968, 0.080, 0.076, 0.88, 0.000],
    [1.000, 0.076, 0.073, 0.90, 0.000],
  ]);

  const wf = makeWeightFn(rig, [
    [0.00, { hips: 1 }],
    [0.12, { hips: 1 }],
    [0.20, { hips: 0.72, spine01: 0.28 }],
    [0.28, { hips: 0.30, spine01: 0.70 }],
    [0.36, { spine01: 0.86, spine02: 0.14 }],
    [0.44, { spine01: 0.42, spine02: 0.58 }],
    [0.52, { spine01: 0.10, spine02: 0.90 }],
    [0.58, { spine02: 0.60, chest: 0.40 }],
    [0.64, { spine02: 0.22, chest: 0.78 }],
    [0.72, { chest: 1 }],
    [0.85, { chest: 0.88, neck: 0.12 }],
    [1.00, { chest: 0.55, neck: 0.45 }],
  ].map(([d, m]) => [d * S, m]));

  const rings = tubeRings(pts, (d, t) => {
    const r = prof(t);
    const g = lerp(G, 1, smooth(0.88, 1.0, t));   // never inflate the collar
    return { rx: r[1] * g * S, ry: r[2] * g * S, round: r[3], dz: r[4] * S };
  }, wf);

  B.setSlot(SLOT.CLOTH).setTint(look.uniform);
  B.setUV(1, look.camoOffset[0] * 0.7, look.camoOffset[1] * 0.7, 0);
  B.loft(rings, { sides: 20, capStart: true, capEnd: true });
  B.setUV(1);
}

/* ================================================================== *
 * NECK + HEAD
 * ================================================================== */
function buildHead(B, rig, look) {
  const S = rig.scale;
  const skin = new THREE.Color(look.skin);
  const hairCol = new THREE.Color(look.hair ?? 0x2b2119);
  const hairMul = [hairCol.r / Math.max(0.02, skin.r), hairCol.g / Math.max(0.02, skin.g),
    hairCol.b / Math.max(0.02, skin.b)];

  /* ---- neck ---------------------------------------------------------
   * The old neck was 0.078 m of radius under a 0.083 m head, so the jaw sat straight
   * on the collar and the two read as one mass — which is most of why the figure
   * measured five heads tall. This one is 0.058 m at the throat and rises 0.104 m
   * clear of the shirt collar, with the sterno-mastoid tapering into the jaw. */
  // The column leans BACK as it rises. The old neck's throat reached z +0.060 while the
  // chin only reached +0.050 — the neck was in front of the jaw, so from any angle the
  // two merged into one mass and there was visually no neck at all.
  const neckPts = resample([
    [0, 1.372, 0.000], [0, 1.420, -0.003], [0, 1.470, -0.007],
    [0, 1.520, -0.010], [0, HEAD.neckTopY, -0.012],
  ].map((p) => [p[0] * S, p[1] * S, p[2] * S]), 7);
  const neckW = makeWeightFn(rig, [
    [0.00, { chest: 0.80, neck: 0.20 }],
    [0.055 * S, { chest: 0.28, neck: 0.72 }],
    [0.105 * S, { neck: 0.86, head: 0.14 }],
    [0.155 * S, { neck: 0.42, head: 0.58 }],
    [0.205 * S, { neck: 0.12, head: 0.88 }],
  ]);
  B.setSlot(SLOT.SKIN).setTint(look.skin).setUV(1.9, 0.31, 0.07, 0.4);
  B.loft(tubeRings(neckPts, (d, t) => ({
    rx: lerp(0.083, 0.0405, smooth(0, 1, t)) * S,
    ry: lerp(0.086, 0.0455, smooth(0, 1, t)) * S,
    round: lerp(0.78, 0.90, t),
  }), neckW), { sides: 14, capStart: false, capEnd: false });

  /* ---- the skull: ONE surface, features are displacements of it ---- */
  const hw = [[rig.index.get('head'), 1]];
  const headWf = makeWeightFn(rig, [
    [HEAD.jawBottomY * S, { head: 0.70, neck: 0.30 }],
    [1.560 * S, { head: 0.88, neck: 0.12 }],
    [1.578 * S, { head: 1 }],
  ]);
  // A finer uv pitch than the body: this is the one surface the camera gets to 30 cm
  // from, and the library's skin pore scale is authored for a whole torso.
  B.setUV(2.35, 0.13, 0.41, 0.0);

  const p = new THREE.Vector3();
  const rows = [];
  const sides = HEAD_SIDES;
  for (const y of HEAD_RINGS) {
    const w = headWf(y * S);
    const row = [];
    let arc = 0;
    let px = 0, pz = 0, first = true;
    for (let j = 0; j <= sides; j++) {
      const th = headAngle(j, sides);
      skullPoint(y, th, p);
      if (!first) arc += Math.hypot(p.x - px, p.z - pz);
      px = p.x; pz = p.z; first = false;
      const tint = faceTint(p.x, p.y, p.z, hairMul);
      const c = [skin.r * tint[0], skin.g * tint[1], skin.b * tint[2]];
      p.multiplyScalar(S);
      const id = B.pushVertex(p, arc * S, y * S, w, c);
      row.push(id);
    }
    B._seams.push(row[0], row[sides]);
    rows.push(row);
  }
  for (let i = 0; i < rows.length - 1; i++) {
    for (let j = 0; j < sides; j++) B.quad(rows[i][j], rows[i][j + 1], rows[i + 1][j + 1], rows[i + 1][j]);
  }
  // crown cap
  {
    const top = rows[rows.length - 1];
    const w = headWf(HEAD.crownY * S);
    const cTint = faceTint(0, HEAD.crownY, -0.02, hairMul);
    const c = [skin.r * cTint[0], skin.g * cTint[1], skin.b * cTint[2]];
    const centre = B.pushVertex(p.set(0, (HEAD.crownY + 0.0035) * S, -0.004 * S), 0, HEAD.crownY * S, w, c);
    for (let j = 0; j < sides; j++) B.tri(centre, top[j], top[j + 1]);
  }

  /* ---- hair ----------------------------------------------------------
   * A cap over the scalp, bounded below by a real HAIRLINE whose height varies with
   * angle: high across the forehead, lower at the temples, lowest at the nape.
   *
   * The first version started at the temples and stood 1 mm proud of the skull, which
   * is the same near-tangent-surfaces mistake as the old face boxes: across the whole
   * forehead the two surfaces are within a millimetre and nearly parallel, and the
   * depth test picks between them per-pixel. In `char_closeup` that drew a soft grey
   * plate straight across the brow and the eyes. The cap now stands a minimum of
   * 3.2 mm off the scalp and never crosses the face at all. */
  {
    const hairlineY = (th) => {
      const c = Math.cos(th), s = Math.abs(Math.sin(th));
      const front = clamp(c, 0, 1), back = clamp(-c, 0, 1);
      return HEAD.hairlineY - 0.030 * s - 0.048 * Math.pow(back, 0.9) + 0.004 * Math.pow(front, 2);
    };
    const hs = 22, hrows = 7;
    const hRows = [];
    B.setTint(look.hair ?? 0x2b2119).setUV(3.1, 0.62, 0.18, 1.1);
    for (let i = 0; i <= hrows; i++) {
      const row = [];
      for (let j = 0; j <= hs; j++) {
        const th = (j % hs) / hs * Math.PI * 2;
        const y0 = hairlineY(th);
        const y = lerp(y0, 1.7885, Math.pow(i / hrows, 0.9));
        // 3.2 mm at the hairline, 6.5 mm over the crown — thick enough never to fight
        // the scalp, thin enough to stay inside the 12 mm helmet clearance
        const infl = 0.0032 + 0.0033 * smooth(y0, 1.7500, y);
        skullPoint(y, th, p, infl);
        p.multiplyScalar(S);
        row.push(B.pushVertex(p, (j / hs) * 0.42 * S, y * S, headWf(y * S)));
      }
      B._seams.push(row[0], row[hs]);
      hRows.push(row);
    }
    for (let i = 0; i < hrows; i++) {
      for (let j = 0; j < hs; j++) B.quad(hRows[i][j], hRows[i][j + 1], hRows[i + 1][j + 1], hRows[i + 1][j]);
    }
    const top = hRows[hrows];
    const centre = B.pushVertex(p.set(0, HEAD.hairTopY * S, -0.004 * S), 0, 0, headWf(HEAD.crownY * S));
    for (let j = 0; j < hs; j++) B.tri(centre, top[j], top[j + 1]);
  }

  /* ---- ears ---- */
  B.setSlot(SLOT.SKIN).setUV(2.35, 0.55, 0.22, 0.8);
  const earMid = (HEAD.earTopY + HEAD.earBotY) * 0.5;
  for (const s of [-1, 1]) {
    B.setTint(new THREE.Color(look.skin).multiplyScalar(1.0).offsetHSL(0, 0.03, -0.02).getHex());
    // pinna: flattened against the skull, tilted back, standing ~7 mm proud
    B.superBox({
      centre: new THREE.Vector3(HEAD.earX * s * S, earMid * S, -0.012 * S),
      quat: new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, -0.13 * s)),
      half: [0.0115 * S, 0.0300 * S, 0.0175 * S], round: 0.62, segU: 10, segV: 7, w: hw,
    });
    // concha: a darker, smaller lobe sunk into it so the ear is not a blank paddle
    B.setTint(new THREE.Color(look.skin).multiplyScalar(0.58).getHex());
    B.superBox({
      centre: new THREE.Vector3((HEAD.earX - 0.004) * s * S, (earMid + 0.001) * S, -0.010 * S),
      half: [0.0100 * S, 0.0150 * S, 0.0092 * S], round: 0.75, segU: 8, segV: 5, w: hw,
    });
    // tragus / lobe
    B.setTint(new THREE.Color(look.skin).multiplyScalar(0.92).getHex());
    B.superBox({
      centre: new THREE.Vector3((HEAD.earX - 0.002) * s * S, (HEAD.earBotY + 0.004) * S, -0.006 * S),
      half: [0.0092 * S, 0.0080 * S, 0.0100 * S], round: 0.85, segU: 7, segV: 4, w: hw,
    });
  }
  B.setUV(1);
}

/* ================================================================== *
 * ARMS
 * ================================================================== */
function buildArm(B, rig, look, side) {
  const S = rig.scale;
  const s = side === 'L' ? -1 : 1;
  const sh = rig.bind['upperArm' + side];
  const el = rig.bind['forearm' + side];
  const wr = rig.bind['hand' + side];

  // start inside the torso so the shoulder never opens a gap
  const p0 = new THREE.Vector3(sh.x * 0.72, sh.y + 0.048 * S, sh.z + 0.004 * S);
  const ctrl = [
    [p0.x, p0.y, p0.z],
    [sh.x, sh.y, sh.z],
    [lerp(sh.x, el.x, 0.45), lerp(sh.y, el.y, 0.45), lerp(sh.z, el.z, 0.45)],
    [el.x, el.y, el.z],
    [lerp(el.x, wr.x, 0.42), lerp(el.y, wr.y, 0.42), lerp(el.z, wr.z, 0.42)],
    [wr.x, wr.y, wr.z],
  ];
  const pts = resample(ctrl, 13);

  const shoulderD = 0;
  const elbowD = p0.distanceTo(sh) + sh.distanceTo(el);
  const wristD = elbowD + el.distanceTo(wr);

  const prof = profileTable([
    [0.00, 0.052, 0.054, 0.86],
    [0.09, 0.068, 0.070, 0.84],
    [0.20, 0.066, 0.068, 0.86],
    [0.34, 0.056, 0.058, 0.88],
    [0.46, 0.050, 0.052, 0.88],
    [0.55, 0.050, 0.053, 0.90],   // elbow — a small bump, never a pinch
    [0.64, 0.050, 0.052, 0.88],
    [0.74, 0.047, 0.049, 0.88],
    [0.86, 0.039, 0.041, 0.88],
    [1.00, 0.031, 0.033, 0.92],
  ]);

  const A = 'upperArm' + side, F = 'forearm' + side, T = 'fTwist' + side, H = 'hand' + side, C = 'clavicle' + side;
  const wf = makeWeightFn(rig, [
    [0.00, { chest: 0.55, [C]: 0.25, [A]: 0.20 }],
    [0.045 * S, { chest: 0.22, [C]: 0.24, [A]: 0.54 }],
    [0.11 * S, { [A]: 0.94, chest: 0.06 }],
    [elbowD - 0.115 * S, { [A]: 1 }],
    [elbowD - 0.055 * S, { [A]: 0.80, [F]: 0.20 }],
    [elbowD, { [A]: 0.50, [F]: 0.50 }],
    [elbowD + 0.055 * S, { [A]: 0.18, [F]: 0.82 }],
    [elbowD + 0.105 * S, { [F]: 1 }],
    [elbowD + 0.150 * S, { [F]: 0.55, [T]: 0.45 }],
    [wristD - 0.075 * S, { [F]: 0.12, [T]: 0.88 }],
    [wristD - 0.030 * S, { [T]: 0.72, [H]: 0.28 }],
    [wristD, { [T]: 0.30, [H]: 0.70 }],
  ]);

  B.setSlot(SLOT.CLOTH).setTint(look.uniform);
  // A different uv rotation and offset per part. The uvs stay metric — texel density is
  // still physically uniform — but the camo tile no longer lines up between the chest,
  // the sleeve and the thigh, which is what let the reviewer find the repeat.
  B.setUV(1, 0.41 * s + look.camoOffset[1] * 0.6, 0.23, 0.62 * s);
  const G = look.girth ?? 1;
  const armG = lerp(1, G, 0.7);
  const rings = tubeRings(pts, (d, t) => {
    const r = prof(t);
    return { rx: r[1] * armG * S, ry: r[2] * armG * S, round: r[3] };
  }, wf);
  B.loft(rings, { sides: 12, capStart: true, capEnd: false });
  B.setUV(1);
  void shoulderD;
}

/**
 * A finger: a tapered tube through a control polyline, with a small radius bump at each
 * joint so the knuckles catch a highlight instead of the whole digit reading as a peg.
 */
function finger(B, ctrl, r0, r1, w, S, segs = 9, sides = 7) {
  const pts = resample(ctrl.map((c) => [c[0] * S, c[1] * S, c[2] * S]), segs);
  let total = 0;
  for (let i = 1; i < pts.length; i++) total += pts[i].distanceTo(pts[i - 1]);
  B.loft(tubeRings(pts, (d) => {
    const t = clamp(d / (total || 1), 0, 1);
    // knuckles at ~1/3 and ~2/3 of the digit
    const k1 = (t - 0.34) / 0.10, k2 = (t - 0.68) / 0.09;
    const knuck = 1 + 0.13 * Math.exp(-(k1 * k1)) + 0.10 * Math.exp(-(k2 * k2));
    const r = lerp(r0, r1, t) * knuck * S;
    return { rx: r, ry: r * 1.06, round: 0.80 };
  }, () => w, { up: new THREE.Vector3(0, 1, 0) }), { sides, capStart: true, capEnd: true });
}

/**
 * Gloved hands with a real grip.
 *
 * BOTH hands are authored in WEAPON space — the same frame as the carbine's own boxes
 * in Gear.js — and then transformed into the hand bone's bind frame. That is the whole
 * trick: `Anim._armIK` pins handR's world rotation to `weaponQuat` and handL's to
 * `weaponQuat * LEFT_GRIP_ROT`, so a point authored at weapon coordinate `p` lands on
 * the weapon at runtime no matter where the arms actually end up. Fingers therefore
 * touch the grip and the handguard in every frame instead of being posed once for one
 * shot and floating in all the others.
 *
 * Right hand: three fingers wrapped round the front of the pistol grip, index on the
 * trigger, thumb opposed round the back. Left hand: palm on the left face of the
 * handguard, four fingers over the top, thumb forward along it — the modern C-clamp.
 */
function buildHand(B, rig, look, side, gear) {
  const S = rig.scale;
  const s = side === 'L' ? -1 : 1;
  const h = rig.bind['hand' + side];
  const w = [[rig.index.get('hand' + side), 1]];
  const gloved = look.gloves;
  B.setSlot(gloved ? SLOT.WEBBING : SLOT.SKIN).setTint(gloved ? look.glove : look.skin);
  B.setUV(2.2, side === 'L' ? 0.37 : 0.71, 0.19, side === 'L' ? 0.9 : -0.7);

  const armed = !!(look.weapon && gear && gear.weaponGrip && gear.weaponLeftGrip);

  if (!armed) {
    // relaxed fist, used only if the loadout carries no weapon
    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(0.12, 0, -0.10 * s));
    B.superBox({
      centre: new THREE.Vector3(h.x, h.y - 0.048 * S, h.z + 0.004 * S),
      quat: q, half: [0.030 * S, 0.052 * S, 0.024 * S], round: 0.45, segU: 12, segV: 6, w,
    });
    for (let k = 0; k < 4; k++) {
      const yk = -0.086 - 0.002 * Math.abs(k - 1.5);
      finger(B, [
        [(0.020 - k * 0.014), yk, 0.016], [(0.019 - k * 0.014), yk - 0.020, 0.028],
        [(0.018 - k * 0.014), yk - 0.032, 0.018],
      ].map((c) => [h.x / S + c[0], h.y / S + c[1], h.z / S + c[2]]), 0.0088, 0.0072, w, S, 7, 6);
    }
    B.setUV(1);
    return;
  }

  /* ---- weapon-space -> hand-bind-space ---- */
  const hr = rig.bind.handR;
  const rot = side === 'R' ? null : LEFT_GRIP_ROT.clone().invert();
  const _p = new THREE.Vector3();
  /**
   * Weapon space -> this hand's bind space.
   *
   * `(x, y, z)` are the SAME coordinates the carbine's boxes use in Gear.js: offsets
   * from `rig.bind.handR`, +Z down the barrel. For the right hand that is already the
   * bind frame, because `Anim` pins handR's world rotation to `weaponQuat`. For the
   * left hand the bone lands on `weaponLeftGrip` with rotation `weaponQuat *
   * LEFT_GRIP_ROT`, so the point has to be re-expressed relative to that grip and then
   * carried back through the inverse of that extra rotation.
   */
  const W = (x, y, z) => {
    _p.set(x * S, y * S, z * S).add(hr);
    if (side === 'L') { _p.sub(gear.weaponLeftGrip).applyQuaternion(rot).add(h); }
    return _p.clone();
  };
  /** weapon-space control point, in unscaled metres, for `finger()` */
  const Wf = (x, y, z) => { const v = W(x, y, z); return [v.x / S, v.y / S, v.z / S]; };
  const Q = (e) => {
    const q = new THREE.Quaternion().setFromEuler(e);
    return side === 'R' ? q : rot.clone().multiply(q);
  };

  if (side === 'R') {
    /* The pistol grip is the box at (0, -0.052, -0.020) half (0.018, 0.052, 0.024):
     * x ±0.018, y -0.104..0.000, z -0.044..+0.004. Everything below wraps that. */
    // back of the hand / metacarpals, on the right face of the grip
    B.superBox({
      centre: W(0.028, -0.038, -0.020),
      quat: Q(new THREE.Euler(0.10, 0.10, -0.12)),
      half: [0.0135 * S, 0.0430 * S, 0.0300 * S], round: 0.46, segU: 12, segV: 7, w,
    });
    // thenar pad filling the web between thumb and index
    B.superBox({
      centre: W(0.021, -0.011, -0.028),
      half: [0.0160 * S, 0.0180 * S, 0.0195 * S], round: 0.62, segU: 9, segV: 5, w,
    });
    // wrist cuff, meeting the sleeve
    B.superBox({
      centre: W(0.006, 0.012, -0.016),
      quat: Q(new THREE.Euler(0.16, 0.10, -0.14)),
      half: [0.0255 * S, 0.0225 * S, 0.0285 * S], round: 0.55, segU: 11, segV: 5, w,
    });
    // index -> trigger
    finger(B, [
      Wf(0.026, -0.006, -0.026), Wf(0.027, -0.008, 0.004),
      Wf(0.019, -0.010, 0.024), Wf(0.004, -0.014, 0.030),
    ], 0.0098, 0.0076, w, S, 9, 7);
    // middle / ring / little wrap the front of the grip
    const yBase = [-0.034, -0.056, -0.077];
    for (let k = 0; k < 3; k++) {
      const y0 = yBase[k];
      const reach = 1 - k * 0.10;
      finger(B, [
        Wf(0.027, y0 + 0.003, -0.032),
        Wf(0.028, y0 - 0.003, 0.002 * reach),
        Wf(0.013 * reach, y0 - 0.010, 0.014 * reach),
        Wf(-0.006 * reach, y0 - 0.015, 0.009 * reach),
      ], 0.0096 - k * 0.0006, 0.0074 - k * 0.0006, w, S, 9, 7);
    }
    // thumb opposed round the back of the grip
    finger(B, [
      Wf(0.024, -0.008, -0.034), Wf(0.017, -0.020, -0.052),
      Wf(0.000, -0.030, -0.058), Wf(-0.014, -0.034, -0.046),
    ], 0.0125, 0.0088, w, S, 8, 7);
  } else {
    /* The handguard is the box at (0, 0.055, 0.222) half (0.025, 0.031, 0.108):
     * x ±0.025, y 0.024..0.086, z 0.114..0.330. The support hand takes it from the
     * left and underneath, fingers over the top. */
    B.superBox({
      centre: W(-0.036, 0.030, 0.207),
      quat: Q(new THREE.Euler(0.05, 0.10, 0.10)),
      half: [0.0130 * S, 0.0330 * S, 0.0430 * S], round: 0.46, segU: 12, segV: 7, w,
    });
    B.superBox({
      centre: W(-0.020, 0.014, 0.198),
      quat: Q(new THREE.Euler(0.10, 0, 0.20)),
      half: [0.0210 * S, 0.0175 * S, 0.0330 * S], round: 0.58, segU: 10, segV: 5, w,
    });
    // wrist cuff — sits back along the forearm's approach
    B.superBox({
      centre: W(-0.014, -0.004, 0.180),
      quat: Q(new THREE.Euler(0.20, 0, 0.24)),
      half: [0.0270 * S, 0.0250 * S, 0.0290 * S], round: 0.55, segU: 11, segV: 5, w,
    });
    for (let k = 0; k < 4; k++) {
      const z = 0.250 - k * 0.0235;
      const over = 1 - k * 0.07;
      finger(B, [
        Wf(-0.036, 0.040, z), Wf(-0.037, 0.070, z + 0.002),
        Wf(-0.026, 0.090 * over, z + 0.003), Wf(-0.004 * over, 0.092 * over, z + 0.002),
      ], 0.0094 - k * 0.0004, 0.0074 - k * 0.0004, w, S, 9, 7);
    }
    // thumb forward along the top-left of the handguard
    finger(B, [
      Wf(-0.032, 0.038, 0.208), Wf(-0.030, 0.058, 0.230),
      Wf(-0.022, 0.074, 0.256), Wf(-0.014, 0.082, 0.280),
    ], 0.0122, 0.0086, w, S, 9, 7);
  }
  B.setUV(1);
}

/* ================================================================== *
 * LEGS + BOOTS
 * ================================================================== */
function buildLeg(B, rig, look, side) {
  const S = rig.scale;
  const hip = rig.bind['thigh' + side];
  const kn = rig.bind['shin' + side];
  const an = rig.bind['foot' + side];

  const ctrl = [
    [hip.x, hip.y + 0.075 * S, hip.z],
    [hip.x, hip.y, hip.z],
    [lerp(hip.x, kn.x, 0.4), lerp(hip.y, kn.y, 0.4), lerp(hip.z, kn.z, 0.4)],
    [kn.x, kn.y, kn.z],
    [lerp(kn.x, an.x, 0.35), lerp(kn.y, an.y, 0.35), lerp(kn.z, an.z, 0.35) + 0.010 * S],
    [an.x, an.y + 0.02 * S, an.z],
  ];
  const pts = resample(ctrl, 14);
  const kneeD = 0.075 * S + hip.distanceTo(kn);
  const ankleD = kneeD + kn.distanceTo(an);

  const prof = profileTable([
    [0.00, 0.098, 0.100, 0.78],
    [0.08, 0.101, 0.103, 0.76],
    [0.22, 0.093, 0.096, 0.78],
    [0.36, 0.083, 0.087, 0.80],
    [0.48, 0.075, 0.079, 0.82],
    [0.55, 0.072, 0.077, 0.84],   // knee
    [0.62, 0.071, 0.077, 0.84],
    [0.70, 0.073, 0.080, 0.82],   // calf
    [0.82, 0.062, 0.066, 0.84],
    [0.92, 0.052, 0.055, 0.86],
    [1.00, 0.048, 0.050, 0.88],
  ]);

  const TH = 'thigh' + side, SN = 'shin' + side, FT = 'foot' + side;
  const wf = makeWeightFn(rig, [
    [0.00, { hips: 0.80, [TH]: 0.20 }],
    [0.055 * S, { hips: 0.42, [TH]: 0.58 }],
    [0.125 * S, { hips: 0.08, [TH]: 0.92 }],
    [kneeD - 0.135 * S, { [TH]: 1 }],
    [kneeD - 0.070 * S, { [TH]: 0.82, [SN]: 0.18 }],
    [kneeD, { [TH]: 0.50, [SN]: 0.50 }],
    [kneeD + 0.070 * S, { [TH]: 0.16, [SN]: 0.84 }],
    [kneeD + 0.130 * S, { [SN]: 1 }],
    [ankleD - 0.080 * S, { [SN]: 1 }],
    [ankleD - 0.030 * S, { [SN]: 0.72, [FT]: 0.28 }],
    [ankleD, { [SN]: 0.40, [FT]: 0.60 }],
  ]);

  const sg = side === 'L' ? -1 : 1;
  const G = lerp(1, look.girth ?? 1, 0.55);
  B.setSlot(SLOT.CLOTH).setTint(look.uniform);
  B.setUV(1, 0.67 + 0.29 * sg, 0.83 * sg + look.camoOffset[0] * 0.5, -0.94 * sg);
  B.loft(tubeRings(pts, (d, t) => {
    const r = prof(t);
    return { rx: r[1] * G * S, ry: r[2] * G * S, round: r[3] };
  }, wf), { sides: 14, capStart: true, capEnd: false });
  B.setUV(1);
}

function buildBoot(B, rig, look, side) {
  const S = rig.scale;
  const an = rig.bind['foot' + side];
  const toe = rig.bind['toe' + side];
  const SN = 'shin' + side, FT = 'foot' + side, TO = 'toe' + side;
  const wShin = [[rig.index.get(SN), 0.62], [rig.index.get(FT), 0.38]];
  const wFoot = [[rig.index.get(FT), 1]];
  const wToe = [[rig.index.get(FT), 0.45], [rig.index.get(TO), 0.55]];

  B.setSlot(SLOT.WEBBING).setTint(look.boot);
  B.setUV(1.6, side === 'L' ? 0.28 : 0.74, 0.55, side === 'L' ? 1.2 : -1.0);

  // ---- shaft: sits over the trouser cuff, blousing at the top ----
  const shaftPts = resample([
    [an.x, 0.330 * S, an.z + 0.006 * S],
    [an.x, 0.270 * S, an.z + 0.004 * S],
    [an.x, 0.200 * S, an.z],
    [an.x, 0.140 * S, an.z - 0.004 * S],
    [an.x, an.y, an.z - 0.006 * S],
  ], 6);
  const shaftW = makeWeightFn(rig, [
    [0.00, { [SN]: 1 }],
    [0.10 * S, { [SN]: 0.94, [FT]: 0.06 }],
    [0.18 * S, { [SN]: 0.62, [FT]: 0.38 }],
    [0.235 * S, { [SN]: 0.35, [FT]: 0.65 }],
  ]);
  B.loft(tubeRings(shaftPts, (d, t) => ({
    rx: lerp(0.078, 0.058, t) * S, ry: lerp(0.081, 0.062, t) * S, round: lerp(0.80, 0.86, t),
  }), shaftW), { sides: 12, capStart: true, capEnd: false });
  void wShin;

  // ---- the foot itself: heel -> toe box ----
  const y0 = 0.052 * S;
  const footPts = resample([
    [an.x, 0.100 * S, an.z - 0.082 * S],
    [an.x, y0 + 0.020 * S, an.z - 0.072 * S],
    [an.x, y0, an.z - 0.020 * S],
    [an.x, y0 - 0.004 * S, toe.z - 0.030 * S],
    [an.x, y0 - 0.008 * S, toe.z + 0.020 * S],
    [an.x, y0 - 0.006 * S, toe.z + 0.048 * S],
  ], 8);
  const footProf = profileTable([
    [0.00, 0.040, 0.042, 0.75],
    [0.18, 0.049, 0.050, 0.65],
    [0.40, 0.052, 0.048, 0.55],
    [0.62, 0.052, 0.045, 0.50],
    [0.85, 0.048, 0.040, 0.50],
    [1.00, 0.030, 0.028, 0.62],
  ]);
  const footW = makeWeightFn(rig, [
    [0.00, { [SN]: 0.45, [FT]: 0.55 }],
    [0.06 * S, { [FT]: 1 }],
    [0.16 * S, { [FT]: 1 }],
    [0.22 * S, { [FT]: 0.62, [TO]: 0.38 }],
    [0.30 * S, { [FT]: 0.35, [TO]: 0.65 }],
  ]);
  B.loft(tubeRings(footPts, (d, t) => {
    const r = footProf(t);
    return { rx: r[1] * S, ry: r[2] * S, round: r[3] };
  }, footW, { up: new THREE.Vector3(0, 1, 0) }), { sides: 12, capStart: true, capEnd: true });

  /* ---- sole ----
   * A boot sole is a midsole (soft, light, chamfered) on an outsole (hard, dark, and
   * narrower than the upper). The old version was one 10 cm slab at `round: 0.30`
   * sticking out past the leather on every side, which is why the reviewer read the
   * boots as unbevelled blocks: at that width the sole IS the silhouette. */
  B.setTint(0x59544a);
  B.superBox({                                            // midsole welt
    centre: new THREE.Vector3(an.x, 0.030 * S, an.z + 0.026 * S),
    half: [0.047 * S, 0.011 * S, 0.112 * S], round: 0.52, segU: 12, segV: 5, w: wFoot,
  });
  B.setTint(look.sole);
  B.superBox({                                            // outsole
    centre: new THREE.Vector3(an.x, 0.014 * S, an.z + 0.028 * S),
    half: [0.044 * S, 0.014 * S, 0.108 * S], round: 0.46, segU: 12, segV: 5, w: wFoot,
  });
  B.superBox({                                            // heel block
    centre: new THREE.Vector3(an.x, 0.011 * S, an.z - 0.058 * S),
    half: [0.042 * S, 0.012 * S, 0.038 * S], round: 0.42, segU: 9, segV: 4, w: wFoot,
  });
  // tread lugs — four, chamfered, inset from the outsole edge
  for (let i = 0; i < 4; i++) {
    const z = lerp(-0.048, 0.126, i / 3) * S + an.z;
    const isToe = z > toe.z - 0.03 * S;
    B.superBox({
      centre: new THREE.Vector3(an.x, 0.004 * S, z),
      half: [0.038 * S, 0.008 * S, 0.015 * S], round: 0.48, segU: 8, segV: 4,
      w: isToe ? wToe : wFoot,
    });
  }
  B.setUV(1);
}

/* ================================================================== *
 * public entry
 * ================================================================== */
export function buildBodyInto(B, rig, look) {
  const gear = weaponAnchors(rig);
  buildTorso(B, rig, look);
  buildHead(B, rig, look);
  for (const s of ['L', 'R']) {
    buildArm(B, rig, look, s);
    buildHand(B, rig, look, s, gear);
    buildLeg(B, rig, look, s);
    buildBoot(B, rig, look, s);
  }
  B.setUV(1);
}
